import {
  FocusableComponentLayout,
  FocusContext,
  setFocus,
} from '@noriginmedia/norigin-spatial-navigation'
import cx from 'classnames'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHybridFocusable } from '../../hooks/useHybridFocusable'
import tts from '../../lib/TTS'
import FocusableKeysTS from '../FocusableButton/FocusableButtonTS'
import { useSidePanel } from '../SidePanel/SidePanelProvider'
import type { KeyboardKeysProps } from './types'
import {
  AlphaCollection,
  KeyBoardDistributionEnum,
  NumericCollection,
} from './types'
import { setKeyProperties } from './utils'

const ALPHA_KEYS = Object.values(AlphaCollection)
const NUMERIC_KEYS = Object.values(NumericCollection)

const PARENT_COMPONENT = 'KEYBOARD_BUTTONS_COMPONENT'

function KeyboardKeys({
  keyboardDistributionKey,
  onEnterPress,
  allowSpaces = true,
  allowDelete = true,
  stealFocus,
  onArrowPress,
}: KeyboardKeysProps) {
  const firstAlphaKey = useMemo(
    () => (allowSpaces ? 'ALPHA_KEY_1' : 'ALPHA_KEY_0'),
    [allowSpaces]
  )
  const firstNumericKey = useMemo(
    () => (allowSpaces ? 'NUMERIC_KEY_1' : 'NUMERIC_KEY_0'),
    [allowSpaces]
  )

  const { ref, focusKey } = useHybridFocusable({
    focusKey: PARENT_COMPONENT,
    onFocus: (_arg1, _arg2, details) => {
      if (
        details &&
        details.event &&
        details.event.type === 'keydown' &&
        (details.event as KeyboardEvent).key === 'ArrowDown'
      ) {
        forceFocus &&
          setFocus(
            keyboardDistributionKey === KeyBoardDistributionEnum.alpha
              ? firstAlphaKey
              : firstNumericKey
          )

        forceFocus && setForceFocus(false)
      }
    },
  })
  const { isSidePanelOpened } = useSidePanel()
  const oldKeyboardToggleKey = useRef<KeyBoardDistributionEnum>(
    keyboardDistributionKey
  )
  const [forceFocus, setForceFocus] = useState(false)

  useEffect(() => {
    if (oldKeyboardToggleKey.current !== keyboardDistributionKey) {
      oldKeyboardToggleKey.current = keyboardDistributionKey
      setForceFocus(true)
    }
  }, [keyboardDistributionKey])

  useEffect(() => {
    if (!isSidePanelOpened || stealFocus) {
      setTimeout(() => {
        setFocus(
          keyboardDistributionKey === KeyBoardDistributionEnum.alpha
            ? firstAlphaKey
            : firstNumericKey
        )
      }, 0)
    }
  }, [isSidePanelOpened, stealFocus])

  const handleKeyFocus = (
    _: FocusableComponentLayout,
    props: { keyTTS: string }
  ) => {
    tts.stop()
    tts.addTextToRead(`Focused on ${props.keyTTS}`)
  }

  const handleKeyEnter = (props: {
    keyString: AlphaCollection | NumericCollection
    keyTTS: string
  }) => {
    const { keyString, keyTTS } = props
    tts.stop()
    tts.addTextToRead(`Selected ${keyTTS}`)
    onEnterPress({ keyString })
  }

  const buttons = useMemo(() => {
    const keys =
      keyboardDistributionKey === KeyBoardDistributionEnum.alpha
        ? ALPHA_KEYS
        : NUMERIC_KEYS
    return keys.filter((key) => {
      if (allowSpaces && allowDelete) return true

      if (
        key === AlphaCollection.spaceAlpha ||
        key === NumericCollection.spaceNumeric
      ) {
        return allowSpaces
      }

      if (
        key === AlphaCollection.deleteAlpha ||
        key === NumericCollection.deleteNumeric
      ) {
        return allowDelete
      }

      return true
    })
  }, [keyboardDistributionKey, allowSpaces, allowDelete])

  return (
    <FocusContext.Provider value={focusKey}>
      <div className="keyboard" ref={ref}>
        <div
          className={cx('keyboard__buttons', {
            'keyboard__buttons--alpha':
              keyboardDistributionKey === KeyBoardDistributionEnum.alpha,
            'keyboard__buttons--numeric':
              keyboardDistributionKey === KeyBoardDistributionEnum.numeric,
          })}
        >
          {buttons.map(
            (singleKey: AlphaCollection | NumericCollection, index: number) => {
              const keyData = setKeyProperties(singleKey)
              const focusKeyId = `${keyboardDistributionKey.toUpperCase()}_KEY_${index}`
              return (
                <FocusableKeysTS
                  className="keyboard__single-button"
                  key={`${keyboardDistributionKey}_KEY_${index}`}
                  focusKey={focusKeyId}
                  onEnterPress={handleKeyEnter}
                  onFocus={handleKeyFocus}
                  onArrowPress={onArrowPress}
                  extraProps={{
                    keyString: singleKey,
                    index,
                    keyTTS: keyData.keyTTS,
                  }}
                >
                  {keyData.keyTxt}
                </FocusableKeysTS>
              )
            }
          )}
        </div>
      </div>
    </FocusContext.Provider>
  )
}

export default KeyboardKeys
